import { PspRules } from "@CommonTypes/backend/PspRules";
import { RulesSection } from "./components/RulesSection";
import { Flex, Grid, HorizontalDivider, Select } from "src/elements";
import { useEffect } from "react";
import { useApi } from "../../api";
import { useAsyncState } from "../../hooks/useAsyncState";
import { RuleToggle } from "./components/RuleToggle";
import { RuleRadios } from "./components/RuleRadios";
import { RuleRange } from "./components/RuleRange";
import { RuleSelect } from "./components/RuleSelect";
import { useTranslation } from "src/i18n";

// The vertical blocking row on this tab only shows what the General tab holds.
const noop = () => undefined;

type BrandBlocksMinBays = NonNullable<PspRules["high_density_mode"]["brand_blocks_min_bays"]>;

// Unset means the backend default, which is this many bays.
export const DEFAULT_BRAND_BLOCKS_MIN_BAYS = 4;
const BRAND_BLOCKS_BAY_CHOICES = [2, 3, 4, 5, 6, 7, 8];

interface Props {
  isDisabled?: boolean;
  merchflowId: number;

  // High Density Mode
  highDensityEnabled: PspRules["high_density_mode"]["enabled"];
  setHighDensityEnabled: (value: PspRules["high_density_mode"]["enabled"]) => void;

  placeAllVariants: PspRules["high_density_mode"]["place_all_variants"];
  setPlaceAllVariants: (value: PspRules["high_density_mode"]["place_all_variants"]) => void;

  snakingStrategy: PspRules["high_density_mode"]["snaking_strategy"];
  setSnakingStrategy: (value: PspRules["high_density_mode"]["snaking_strategy"]) => void;

  variantContiguous: PspRules["high_density_mode"]["variant_contiguous"];
  setVariantContiguous: (value: PspRules["high_density_mode"]["variant_contiguous"]) => void;

  allowBreakingVariantSequencing: PspRules["high_density_mode"]["allow_breaking_variant_sequencing"];
  setAllowBreakingVariantSequencing: (
    value: PspRules["high_density_mode"]["allow_breaking_variant_sequencing"],
  ) => void;

  // Read only: vertical blocking is one switch, on the General tab. The mudmap
  // has to be planned in columns for the core to block vertically, so the two
  // can no longer be set apart from each other.
  ameVerticalBlockingMode: PspRules["high_density_mode"]["vertical_blocking_mode"];

  brandBlocksMinBays: PspRules["high_density_mode"]["brand_blocks_min_bays"];
  setBrandBlocksMinBays: (value: BrandBlocksMinBays) => void;

  verticalSplitSection: PspRules["fixture"]["vertical_split_section"];
  setVerticalSplitSection: (value: PspRules["fixture"]["vertical_split_section"]) => void;

  // High Density Building Blocks Mode
  buildingBlocksEnabled: PspRules["high_density_building_blocks_mode"]["enabled"];
  setBuildingBlocksEnabled: (
    value: PspRules["high_density_building_blocks_mode"]["enabled"],
  ) => void;

  buildingBlocksDirection: PspRules["high_density_building_blocks_mode"]["direction"];
  setBuildingBlocksDirection: (
    value: PspRules["high_density_building_blocks_mode"]["direction"],
  ) => void;

  startFromType: PspRules["high_density_building_blocks_mode"]["start_from"];
  setStartFromType: (value: PspRules["high_density_building_blocks_mode"]["start_from"]) => void;
}

export const UhdPlanogramRules = ({
  isDisabled,
  merchflowId,

  // High Density Mode
  highDensityEnabled,
  setHighDensityEnabled,
  placeAllVariants,
  setPlaceAllVariants,
  snakingStrategy,
  setSnakingStrategy,
  variantContiguous,
  setVariantContiguous,
  allowBreakingVariantSequencing,
  setAllowBreakingVariantSequencing,
  ameVerticalBlockingMode,
  brandBlocksMinBays,
  setBrandBlocksMinBays,
  verticalSplitSection,
  setVerticalSplitSection,

  // High Density Building Blocks Mode
  buildingBlocksEnabled,
  setBuildingBlocksEnabled,
  buildingBlocksDirection,
  setBuildingBlocksDirection,
  startFromType,
  setStartFromType,
}: Props) => {
  const { t } = useTranslation("common");
  const { getMerchflowUhdBuildingBlocksCompletedStoresApi } = useApi();
  const [storeCodes, setStoreCodes] = useAsyncState<string[]>();

  const loadStores = async () => {
    const response = await getMerchflowUhdBuildingBlocksCompletedStoresApi(merchflowId);
    if (!response) {
      setStoreCodes(null);
      return;
    }

    setStoreCodes(response.map((store) => store.store_code));
  };

  useEffect(() => {
    loadStores();
  }, [merchflowId]);

  // A stored count outside the list (1, say) stays listed so it is shown and kept, not replaced.
  const bayChoices =
    typeof brandBlocksMinBays === "number" && !BRAND_BLOCKS_BAY_CHOICES.includes(brandBlocksMinBays)
      ? [...BRAND_BLOCKS_BAY_CHOICES, brandBlocksMinBays].sort((a, b) => a - b)
      : BRAND_BLOCKS_BAY_CHOICES;
  const brandBlocksOptions: Array<{ label: string; value: BrandBlocksMinBays }> = [
    ...bayChoices.map((count) => ({
      label:
        count === 1
          ? t("planogramRules.uhd.brandBlocks.oneBay")
          : t("planogramRules.uhd.brandBlocks.bays", { count }),
      value: count,
    })),
    { label: t("planogramRules.uhd.brandBlocks.never"), value: "none" },
  ];

  return (
    <Grid
      width="100%"
      gap="20px"
      areas={`
        "high-density high-density-building-blocks"
      `}
      columns="1fr 1fr"
    >
      <RulesSection
        title="Auto-Mudmap Engine (UHD Mode)"
        path="high_density_mode.enabled"
        area="high-density"
        toggleValue={highDensityEnabled}
        setToggleValue={(highDensityEnabled) => {
          setHighDensityEnabled(highDensityEnabled);

          // Building blocks is an additional mode on top of the engine, so it
          // cannot stay enabled without it.
          if (!highDensityEnabled) {
            setBuildingBlocksEnabled(false);
          }
        }}
        isDisabled={isDisabled}
      >
        <RuleRadios
          title="How should snaking be applied across sections?"
          path="high_density_mode.snaking_strategy"
          direction="column"
          options={[
            { label: "No Snaking", value: "none" },
            { label: "Snaking on Variant Spanning Only", value: "variant_spanning_sections" },
            { label: "Always Snaking", value: "always" },
          ]}
          value={snakingStrategy}
          setValue={setSnakingStrategy}
          isDisabled={isDisabled || !highDensityEnabled}
        />

        <HorizontalDivider />

        <RuleToggle
          title="When there are variant restrictions on shelves, can we round off variants on previous shelves?"
          path="high_density_mode.variant_contiguous"
          value={variantContiguous}
          setValue={setVariantContiguous}
          isDisabled={isDisabled || !highDensityEnabled}
        />

        <HorizontalDivider />

        <RuleToggle
          title="Should we place all variants on the mudmap?"
          path="high_density_mode.place_all_variants"
          value={placeAllVariants}
          setValue={setPlaceAllVariants}
          isDisabled={isDisabled || !highDensityEnabled}
        />

        <HorizontalDivider />

        <RuleToggle
          title="Allow breaking variant sequencing?"
          path="high_density_mode.allow_breaking_variant_sequencing"
          value={allowBreakingVariantSequencing}
          setValue={setAllowBreakingVariantSequencing}
          isDisabled={isDisabled || !highDensityEnabled}
        />

        <HorizontalDivider />

        <RuleToggle
          title="Use vertical blocking mode?"
          path="high_density_mode.vertical_blocking_mode"
          description="Set on the General tab, under Merchandising."
          value={ameVerticalBlockingMode}
          setValue={noop}
          isDisabled
        />

        <HorizontalDivider />

        <RuleSelect<BrandBlocksMinBays>
          testId="brand-blocks-min-bays"
          title={t("planogramRules.uhd.brandBlocks.title")}
          description={t(
            brandBlocksMinBays === "none"
              ? "planogramRules.uhd.brandBlocks.descriptionNever"
              : "planogramRules.uhd.brandBlocks.description",
          )}
          options={brandBlocksOptions}
          value={brandBlocksMinBays ?? DEFAULT_BRAND_BLOCKS_MIN_BAYS}
          setValue={setBrandBlocksMinBays}
          isDisabled={isDisabled || !highDensityEnabled || !ameVerticalBlockingMode}
        />

        <HorizontalDivider />

        <RuleRange
          title="Vertical Split Section"
          path="fixture.vertical_split_section"
          description={t("planogramRules.uhd.verticalSplitDescription")}
          min={1}
          max={5}
          step={1}
          value={verticalSplitSection}
          setValue={setVerticalSplitSection}
          leftLabel="1"
          rightLabel="5"
          isDisabled={isDisabled || !highDensityEnabled}
        />
      </RulesSection>

      <RulesSection
        title="Auto-Mudmap Engine Building Blocks (UHD Building Blocks)"
        path="high_density_building_blocks_mode.enabled"
        area="high-density-building-blocks"
        toggleValue={buildingBlocksEnabled}
        setToggleValue={(buildingBlocksEnabled) => {
          setBuildingBlocksEnabled(buildingBlocksEnabled);

          // Building blocks runs in addition to the engine, which still has to
          // be configured and used.
          if (buildingBlocksEnabled) {
            setHighDensityEnabled(true);
          }
        }}
        isDisabled={isDisabled}
      >
        <RuleRadios
          title="Select how to build the building blocks"
          path="high_density_building_blocks_mode.direction"
          direction="column"
          options={[
            { label: "Backward", value: "BACKWARD" },
            { label: "Forward", value: "FORWARD" },
          ]}
          value={buildingBlocksDirection}
          setValue={setBuildingBlocksDirection}
          isDisabled={isDisabled || !buildingBlocksEnabled}
        />

        <HorizontalDivider />

        <RuleRadios
          title="Where should we start the store pog generation?"
          path="high_density_building_blocks_mode.start_from"
          direction="column"
          options={
            [
              { label: "Start from biggest", value: "BIGGEST" },
              { label: "Continue from last completed pog", value: "LAST_COMPLETED" },
              {
                label: "Select from sequence",
                value: "STORE_CODE",
                isDisabled: !storeCodes || storeCodes.length === 0,
              },
            ] satisfies Array<{
              label: string;
              value: PspRules["high_density_building_blocks_mode"]["start_from"]["type"];
              isDisabled?: boolean;
            }>
          }
          value={startFromType.type}
          setValue={(type) => {
            if (type === "STORE_CODE") {
              if (storeCodes && storeCodes.length > 0) {
                setStartFromType({ type, store_code: storeCodes[0] });
              }
            } else {
              setStartFromType({ type });
            }
          }}
          isDisabled={isDisabled || !buildingBlocksEnabled}
        />
        {startFromType.type === "STORE_CODE" && storeCodes && storeCodes.length > 0 && (
          <Flex padding="0 10px">
            <Select
              title="Store"
              options={storeCodes.map((storeCode) => ({ label: storeCode, value: storeCode }))}
              value={startFromType.store_code}
              setValue={(storeCode) => {
                setStartFromType({ type: "STORE_CODE", store_code: storeCode });
              }}
            />
          </Flex>
        )}
      </RulesSection>
    </Grid>
  );
};
